"use client";

import {JSX, useEffect} from "react";

import {RentalOfferShowMore} from "@/sections/rental/rental-offer/variants";
import type {SpaceProps} from "@/sections/use-section-space";

import type {OurPropertySectionData} from "@/features/flexible/our-property/type";
import {PropertyListModel} from "@/features/rental/property-list/property-list.model";
import {SimpleSearchView} from "@/features/rental/simple-search/ui/simple-search.view";

import {MOBILE_MAX, TABLET_MAX, useMatchMedia} from "@/shared/hooks/dom/use-match-media";
import {useModel} from "@/shared/hooks/state-management/use-model";
import {useTranslation} from "@/shared/i18n";
import {ButtonBase} from "@/shared/ui/buttons/button-base";

import type {DestinationRentalsData} from "./models/types";

import "./styles/destination-rentals-search.scss";

export interface DestinationRentalsViewProps extends DestinationRentalsData {
    space?: SpaceProps;
    anchorId?: string;
    showSearchFilters?: boolean;
}

export function DestinationRentalsView({
    places,
    total,
    pages,
    searchUrl,
    viewAllUrl,
    title,
    subtitle,
    space,
    anchorId,
    showSearchFilters = false,
}: DestinationRentalsViewProps): JSX.Element {
    const isMobile = useMatchMedia({maxWidth: MOBILE_MAX});
    const isTablet = useMatchMedia({maxWidth: TABLET_MAX});
    const {t} = useTranslation("common");

    const sectionData: OurPropertySectionData = {
        _type: "ourPropertySection",
        _key: "destination-rentals",
        sectionType: "suggestions",
        suggestionsContentType: "searchUrl",
        searchUrl,
        title,
        subtitle,
        space,
    };

    const [model] = useModel(PropertyListModel, {
        sectionData,
        initialPlaces: places,
        initialTotal: total,
        initialPages: pages,
        initialSearchUrl: searchUrl,
        initialViewAllUrl: viewAllUrl,
        showCount: 4,
    });

    useEffect(() => {
        if (isMobile) {
            model.setItemsPerPage(2);
        } else if (isTablet) {
            model.setItemsPerPage(3);
        } else {
            model.setItemsPerPage(4);
        }
    }, [isMobile, isTablet]);

    return (
        <RentalOfferShowMore
            properties={model.visibleProperties}
            loading={{
                isFiltering: model.isFiltering,
                isInitial: model.isLoading,
            }}
            anchorId={anchorId}
            showButton={model.showButton}
            isPaginationLoading={model.isPaginationLoading}
            showReadLessButton={model.showReadLessButton}
            onLoadMore={() => void model.loadMore()}
            onShowLess={() => model.showLess()}
            space={space}
            content={{title, subtitle}}
            viewAllLink={showSearchFilters ? undefined : {url: model.viewAllUrl}}
            headerExtra={
                showSearchFilters ? (
                    <div className="destination-rentals-search">
                        <SimpleSearchView
                            showLocation={false}
                            onSubmit={(params) => void model.applyDatesGuestsFilter(params)}
                            submitIcon="refresh"
                        />
                        {model.viewAllUrl && (
                            <ButtonBase
                                variant="primary"
                                size="sm"
                                width="auto"
                                as="link"
                                href={model.viewAllUrl}
                                className="destination-rentals-search__view-all"
                            >
                                {t("viewAll")}
                            </ButtonBase>
                        )}
                    </div>
                ) : undefined
            }
            disableSlice
        />
    );
}
